import { TFileClassification } from "@/api/services/main/talent/dtos/generate-scan-image.dto";

// System attachment document types eligible for AI auto-classification.
// 1=在留カード（表）, 2=在留カード（裏）, 3=履歴書, 4=卒業証書,
// 5=特定技能合格証等, 6=技能実習修了書等, 7=日本語合格証.
// Type 8 (写真) and admin custom types are intentionally excluded.
const ALLOWED_DOCUMENT_TYPE_IDS = new Set<number>([1, 2, 3, 4, 5, 6, 7]);

// Fallback when AI can't classify a file. 3 = 履歴書 (resume) is the most
// common attachment type in this flow; users can override via the dropdown
// before saving. Avoids silent submit blocks from yup `.required()` on null.
const DEFAULT_DOCUMENT_TYPE_ID = "3";

/**
 * Map each uploaded file to its AI-classified attachment document type id,
 * keyed by filename. Returns `string` ids so the value flows directly into
 * the `TTalentForm.relatedFileTypes: string[]` slot consumed by the form
 * dropdown + yup schema.
 *
 * Guarantees `result.length === files.length` so the backend never receives
 * a length-mismatched `relatedFileTypes` array (root cause of the
 * `bigint: "NaN"` insert error on `talent_upload_files`).
 *
 * Falls back to DEFAULT_DOCUMENT_TYPE_ID per file when:
 * - `classification` is missing/empty (legacy BE before prompt v2.6)
 * - filename is not present in classification (BE sanitizer dropped it)
 * - documentTypeId is outside the allowed 1-7 set
 * - AI explicitly returned null
 *
 * The default keeps form validation green so a save isn't silently blocked
 * when the AI hesitates; the user still sees + can edit each type before submit.
 */
export const buildRelatedFileTypes = (files: File[], classification: TFileClassification[] | undefined): string[] => {
  if (!classification?.length) return files.map(() => DEFAULT_DOCUMENT_TYPE_ID);

  const byFilename = new Map<string, string>();
  for (const entry of classification) {
    if (!entry?.filename) continue;
    if (byFilename.has(entry.filename)) continue; // first match wins on dup
    const id = entry.documentTypeId;
    const valid = typeof id === "number" && ALLOWED_DOCUMENT_TYPE_IDS.has(id) ? String(id) : DEFAULT_DOCUMENT_TYPE_ID;
    byFilename.set(entry.filename, valid);
  }

  return files.map((f) => byFilename.get(f.name) ?? DEFAULT_DOCUMENT_TYPE_ID);
};
